/**
 * Kansas State-Specific Diagnostic Questions
 * All questions reference actual KS regulations, forms, and requirements
 *
 * LICENSING AUTHORITY: Kansas Department of Health and Environment (KDHE)
 * Primary Code: Kansas Administrative Regulations (K.A.R.) 28-51 (Home Health Agency Licensure)
 * Statute: K.S.A. 65-5102 (Requires HHAs to be licensed - effective July 1, 2017)
 *
 * LICENSE TYPES:
 *   1. Skilled Services License - Skilled nursing, therapy, all HHA services
 *   2. Non-Medical Supportive Care Services License - Chore services only
 *
 * BACKGROUND CHECKS: K.S.A. 65-5117 (through KBI, every 2 years)
 * ABUSE REPORTING: K.S.A. 39-1431 (DCF Adult Protective Services)
 *
 * NOTE: KDADS handles Adult Care Homes and HCBS waivers, NOT home health agency licensing
 */

import type { DiagnosticQuestionConfig } from '../../../types/diagnostic';

export const KANSAS_QUESTIONS: DiagnosticQuestionConfig[] = [
  // ============================================
  // SECTION A: LICENSING & OPERATIONS
  // ============================================
  {
    id: 'KS-A1',
    itemId: 'A1',
    stateCode: 'KS',
    title: 'KDHE Home Health Agency License Status',
    description: 'Kansas requires all home health agencies to be licensed by KDHE under K.A.R. 28-51',
    severity: 'high',
    regulationCode: '28-51-100',
    regulationName: 'Licensure',
    agencyName: 'Kansas Department of Health and Environment',
    agencyAbbr: 'KDHE',
    agencyPhone: '(785) 296-1086',
    agencyUrl: 'https://www.kdhe.ks.gov/582/Home-Health-Agencies',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a current, active Home Health Agency license from KDHE?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Home Health licenses must be renewed annually per K.S.A. 65-5104',
      },
      {
        id: 'q2',
        question: 'What is your KDHE license expiration date?',
        inputType: 'date',
        helpText: 'Found on your KDHE home health license certificate',
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'daysFuture', questionId: 'q2', value: 90, result: 'COMPLIANT' },
      { condition: 'daysFuture', questionId: 'q2', value: 45, result: 'AT_RISK' },
      { condition: 'daysFuture', questionId: 'q2', value: 0, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Your KDHE Home Health Agency license is current and active per K.A.R. 28-51.',
      atRisk: 'Your license expires within 90 days. Kansas requires renewal applications to be submitted to KDHE before expiration. Apply immediately.',
      nonCompliant: 'Operating a home health agency in Kansas without a valid KDHE license violates K.A.R. 28-51 and can result in civil penalties up to $500/day. Contact KDHE immediately at (785) 296-1086.',
      unknown: 'Verify your license status with KDHE at (785) 296-1086.',
    },
    stateNotes: [
      'Home Health licenses are valid for 1 year (annual renewal)',
      'Administrator must have bachelor\'s degree or equivalent healthcare experience',
      'Agencies must provide 24-hour availability for patient needs',
      'License must be posted in a conspicuous location',
    ],
  },

  {
    id: 'KS-A2',
    itemId: 'A2',
    stateCode: 'KS',
    title: 'Administrator Qualifications (K.A.R. 28-51-101)',
    description: 'Kansas requires qualified administrator for all home health agencies',
    severity: 'high',
    regulationCode: '28-51-101',
    regulationName: 'Administrator',
    agencyName: 'Kansas Department of Health and Environment',
    agencyAbbr: 'KDHE',
    agencyPhone: '(785) 296-1086',
    agencyUrl: 'https://www.kdhe.ks.gov/582/Home-Health-Agencies',
    questions: [
      {
        id: 'q1',
        question: 'Does your agency administrator meet KDHE requirements? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'designated', label: 'Designated administrator on file with KDHE' },
          { value: 'education', label: 'Bachelor\'s degree or equivalent healthcare experience' },
          { value: 'experience', label: 'At least 1 year supervisory experience in healthcare' },
          { value: 'fulltime', label: 'Available during business hours' },
          { value: 'training', label: 'Completed required administrator training' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 5, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 3, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 3, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Administrator qualifications meet KDHE requirements per K.A.R. 28-51-101.',
      atRisk: 'Administrator qualifications may not fully meet KDHE requirements. Review credentials and notify KDHE of any changes.',
      nonCompliant: 'Administrator does not meet KDHE qualification requirements. This is a licensure violation. Contact KDHE for guidance.',
      unknown: 'Verify administrator qualifications against K.A.R. 28-51-101 requirements.',
    },
    stateNotes: [
      'Changes in administrator must be reported to KDHE within 30 days',
      'Administrator must be available during normal business hours',
      'KDHE must be notified of administrator absence exceeding 30 days',
    ],
  },

  {
    id: 'KS-A3',
    itemId: 'A3',
    stateCode: 'KS',
    title: 'Insurance Requirements (K.A.R. 28-51-100)',
    description: 'Kansas requires specific insurance coverage for home health agencies',
    severity: 'high',
    regulationCode: '28-51-100',
    regulationName: 'Insurance',
    agencyName: 'Kansas Department of Health and Environment',
    agencyAbbr: 'KDHE',
    agencyPhone: '(785) 296-1086',
    agencyUrl: 'https://www.kdhe.ks.gov/582/Home-Health-Agencies',
    questions: [
      {
        id: 'q1',
        question: 'What is your general liability insurance per-occurrence coverage amount?',
        inputType: 'number',
        required: true,
        helpText: 'Kansas requires minimum $1,000,000 per occurrence',
      },
      {
        id: 'q2',
        question: 'What is your general liability insurance aggregate coverage amount?',
        inputType: 'number',
        required: true,
        helpText: 'Kansas requires minimum $3,000,000 aggregate',
      },
      {
        id: 'q3',
        question: 'Do you maintain professional liability/malpractice insurance?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q4',
        question: 'Do you maintain workers\' compensation insurance covering all employees?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Workers\' compensation is required in Kansas per K.S.A. 44-532',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'greaterOrEqual', questionId: 'q1', value: 1000000 },
        { condition: 'greaterOrEqual', questionId: 'q2', value: 3000000 },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
        { condition: 'equals', questionId: 'q4', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'greaterOrEqual', questionId: 'q1', value: 1000000 },
        { condition: 'greaterOrEqual', questionId: 'q2', value: 2000000 },
        { condition: 'equals', questionId: 'q4', value: 'yes' },
      ], result: 'AT_RISK' },
      { condition: 'anyTrue', value: [
        { condition: 'lessThan', questionId: 'q1', value: 1000000 },
        { condition: 'equals', questionId: 'q4', value: 'no' },
      ], result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Your insurance coverage meets Kansas K.A.R. 28-51-100 requirements.',
      atRisk: 'Your insurance may not meet all Kansas requirements. Review coverage with KDHE.',
      nonCompliant: 'Your insurance coverage does not meet Kansas requirements. Missing workers\' compensation or inadequate liability coverage can result in license revocation.',
      unknown: 'Verify your insurance meets K.A.R. 28-51-100 requirements.',
    },
    stateNotes: [
      'Kansas requires $1M per occurrence AND $3M aggregate liability',
      'Workers\' compensation required per K.S.A. 44-532',
      'Professional liability required for skilled nursing services',
      'Proof of insurance required with license application and renewal',
      'Fidelity bond recommended for financial protection',
    ],
  },

  {
    id: 'KS-A4',
    itemId: 'A4',
    stateCode: 'KS',
    title: 'Written Policies and Procedures (K.A.R. 28-51-102)',
    description: 'Kansas requires specific written policies per K.A.R. 28-51-102',
    severity: 'medium',
    regulationCode: '28-51-102',
    regulationName: 'Policies',
    agencyName: 'Kansas Department of Health and Environment',
    agencyAbbr: 'KDHE',
    agencyPhone: '(785) 296-1086',
    agencyUrl: 'https://www.kdhe.ks.gov/582/Home-Health-Agencies',
    questions: [
      {
        id: 'q1',
        question: 'Which of the following Kansas-required policies does your agency have documented? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'patient_rights', label: 'Patient rights and responsibilities' },
          { value: 'admission', label: 'Admission, transfer, and discharge criteria' },
          { value: 'plan_of_care', label: 'Plan of care development and review' },
          { value: 'supervision', label: 'Staff supervision requirements' },
          { value: 'emergency', label: 'Emergency procedures' },
          { value: 'infection', label: 'Infection control procedures' },
          { value: 'complaint', label: 'Complaint and grievance procedures' },
          { value: 'abuse', label: 'Abuse, neglect, and exploitation reporting' },
          { value: 'confidentiality', label: 'Patient confidentiality (HIPAA)' },
          { value: 'quality', label: 'Quality assurance program' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 10, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 7, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 7, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Your policy manual meets Kansas requirements per K.A.R. 28-51-102.',
      atRisk: 'Your policy manual is missing required sections. KDHE surveyors will review policies. Missing: {missing}.',
      nonCompliant: 'Multiple required policies are missing per K.A.R. 28-51-102. Develop policies for: {missing}.',
      unknown: 'Review your policies against K.A.R. 28-51-102 requirements.',
    },
    stateNotes: [
      'K.A.R. 28-51-102 requires written policies and procedures',
      'Policies must be reviewed and updated annually',
      'Patient rights policy must comply with K.S.A. 65-5108',
      'KDHE surveyors review policies during annual inspections',
    ],
  },

  {
    id: 'KS-A5',
    itemId: 'A5',
    stateCode: 'KS',
    title: 'Incident and Abuse Reporting (K.S.A. 39-1431)',
    description: 'Kansas requires documented incident and mandatory abuse reporting procedures',
    severity: 'high',
    regulationCode: '39-1431',
    regulationName: 'Abuse Reporting',
    agencyName: 'Kansas Department for Children and Families',
    agencyAbbr: 'DCF',
    agencyPhone: '(800) 922-5330',
    agencyUrl: 'https://www.dcf.ks.gov/services/PPS/Pages/APS.aspx',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a documented incident and abuse reporting process?',
        inputType: 'select',
        options: [
          { value: 'documented', label: 'Yes, fully documented with DCF/APS hotline information available' },
          { value: 'informal', label: 'Yes, but informal / not fully documented' },
          { value: 'no', label: 'No incident reporting process in place' },
        ],
        required: true,
        helpText: 'K.S.A. 39-1431 requires reporting of suspected adult abuse/neglect',
      },
      {
        id: 'q2',
        question: 'Which types of incidents does your reporting process cover? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'physical', label: 'Physical abuse' },
          { value: 'neglect', label: 'Neglect or self-neglect' },
          { value: 'financial', label: 'Financial exploitation' },
          { value: 'emotional', label: 'Psychological/emotional abuse' },
          { value: 'sexual', label: 'Sexual abuse' },
          { value: 'injury', label: 'Patient injuries during service' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'documented' },
        { condition: 'countAtLeast', questionId: 'q2', value: 5 },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'informal' },
        { condition: 'countAtLeast', questionId: 'q2', value: 3 },
      ], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Incident and abuse reporting process is documented per K.S.A. 39-1431.',
      atRisk: 'Incident reporting process exists but may not be fully documented. Ensure DCF/APS hotline (800) 922-5330 is available to all staff.',
      nonCompliant: 'No incident reporting process is in place. K.S.A. 39-1431 requires reporting of suspected abuse. All staff are mandated reporters.',
      unknown: 'Verify your incident reporting procedures meet K.S.A. 39-1431 requirements.',
    },
    stateNotes: [
      'Kansas Adult Protective Services Hotline: (800) 922-5330',
      'Reports can be made 24/7',
      'All home health workers are mandated reporters under K.S.A. 39-1431',
      'Failure to report is a Class B misdemeanor',
      'Must also check Kansas Nurse Aide Registry for abuse findings',
    ],
  },

  {
    id: 'KS-A6',
    itemId: 'A6',
    stateCode: 'KS',
    title: 'Emergency Preparedness Plan (K.A.R. 28-51-102)',
    description: 'Kansas requires documented emergency preparedness procedures',
    severity: 'medium',
    regulationCode: '28-51-102',
    regulationName: 'Emergency Preparedness',
    agencyName: 'Kansas Department of Health and Environment',
    agencyAbbr: 'KDHE',
    agencyPhone: '(785) 296-1086',
    agencyUrl: 'https://www.kdhe.ks.gov/582/Home-Health-Agencies',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a documented emergency preparedness plan?',
        inputType: 'select',
        options: [
          { value: 'documented', label: 'Yes, comprehensive written plan addressing Kansas hazards' },
          { value: 'partial', label: 'Partial plan, needs updating' },
          { value: 'no', label: 'No emergency preparedness plan' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Does your emergency plan address Kansas-specific hazards? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'tornado', label: 'Tornado preparedness and sheltering' },
          { value: 'severe_weather', label: 'Severe thunderstorm/ice storm response' },
          { value: 'flood', label: 'Flood response' },
          { value: 'patient_contact', label: 'Patient emergency contact procedures' },
          { value: 'staff_notification', label: 'Staff notification procedures' },
          { value: 'continuity', label: 'Service continuity plans' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'documented' },
        { condition: 'countAtLeast', questionId: 'q2', value: 5 },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'partial' },
        { condition: 'countAtLeast', questionId: 'q2', value: 3 },
      ], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Emergency preparedness plan is comprehensive and addresses Kansas-specific hazards per K.A.R. 28-51-102.',
      atRisk: 'Emergency preparedness plan is incomplete. Include procedures for tornadoes, severe weather, and service continuity.',
      nonCompliant: 'No emergency preparedness plan is in place. This violates K.A.R. 28-51-102.',
      unknown: 'Review your emergency preparedness procedures for Kansas-specific hazards.',
    },
    stateNotes: [
      'Kansas is in Tornado Alley - tornado preparedness is critical',
      'Plan for severe thunderstorms and ice storms common in Kansas',
      'Consider rural service areas with limited communication',
      'Patient priority lists for emergency contact',
    ],
  },

  {
    id: 'KS-A7',
    itemId: 'A7',
    stateCode: 'KS',
    title: 'Quality Assurance Program (K.A.R. 28-51-103)',
    description: 'Kansas requires ongoing quality assessment and performance improvement',
    severity: 'medium',
    regulationCode: '28-51-103',
    regulationName: 'Quality Assurance',
    agencyName: 'Kansas Department of Health and Environment',
    agencyAbbr: 'KDHE',
    agencyPhone: '(785) 296-1086',
    agencyUrl: 'https://www.kdhe.ks.gov/582/Home-Health-Agencies',
    questions: [
      {
        id: 'q1',
        question: 'Do you conduct and document quality assurance reviews?',
        inputType: 'select',
        options: [
          { value: 'regular', label: 'Yes, regular documented reviews (at least quarterly)' },
          { value: 'occasional', label: 'Occasional reviews, not consistently documented' },
          { value: 'no', label: 'No quality assurance reviews' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'regular', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'occasional', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Quality assurance program meets K.A.R. 28-51-103 requirements.',
      atRisk: 'Quality assurance reviews are not conducted consistently. Implement regular documented reviews.',
      nonCompliant: 'No quality assurance program exists. This violates K.A.R. 28-51-103.',
      unknown: 'Review quality assurance requirements under K.A.R. 28-51-103.',
    },
    stateNotes: [
      'K.A.R. 28-51-103 requires quality assessment and performance improvement',
      'Quarterly reviews are recommended best practice',
      'Document all QA activities and corrective actions taken',
      'Include clinical record reviews and patient outcome tracking',
    ],
  },

  {
    id: 'KS-A8',
    itemId: 'A8',
    stateCode: 'KS',
    title: 'Record Retention (K.A.R. 28-51-108)',
    description: 'Kansas requires minimum 5-year record retention',
    severity: 'medium',
    regulationCode: '28-51-108',
    regulationName: 'Record Retention',
    agencyName: 'Kansas Department of Health and Environment',
    agencyAbbr: 'KDHE',
    agencyPhone: '(785) 296-1086',
    agencyUrl: 'https://www.kdhe.ks.gov/582/Home-Health-Agencies',
    questions: [
      {
        id: 'q1',
        question: 'How long do you retain patient and employee records?',
        inputType: 'select',
        options: [
          { value: '5plus', label: '5 years or longer (meets KS requirement)' },
          { value: '3to4', label: '3-4 years' },
          { value: 'less3', label: 'Less than 3 years' },
          { value: 'unsure', label: 'Not sure / no formal policy' },
        ],
        required: true,
        helpText: 'Kansas K.A.R. 28-51-108 requires minimum 5-year retention',
      },
    ],
    scoringRules: [
      { condition: 'equals', value: '5plus', result: 'COMPLIANT' },
      { condition: 'equals', value: '3to4', result: 'AT_RISK' },
      { condition: 'includes', value: ['less3', 'unsure'], result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Your record retention policy meets Kansas\'s 5-year requirement under K.A.R. 28-51-108.',
      atRisk: 'Kansas requires 5-year record retention. Update your policy to ensure compliance.',
      nonCompliant: 'Your record retention does not meet Kansas\'s 5-year requirement. Immediately update your retention policy.',
      unknown: 'Review record retention requirements under K.A.R. 28-51-108.',
    },
    stateNotes: [
      'Kansas requires 5-year retention (vs CA/HI 7 years)',
      'Records for minors require longer retention',
      'Both patient and employee records subject to requirement',
    ],
  },

  // ============================================
  // SECTION B: PERSONNEL FILES
  // ============================================
  {
    id: 'KS-B1',
    itemId: 'B1',
    stateCode: 'KS',
    title: 'Personnel File Requirements (K.A.R. 28-51-105)',
    description: 'Kansas requires specific documents in each staff member\'s personnel file',
    severity: 'high',
    regulationCode: '28-51-105',
    regulationName: 'Personnel',
    agencyName: 'Kansas Department of Health and Environment',
    agencyAbbr: 'KDHE',
    agencyPhone: '(785) 296-1086',
    agencyUrl: 'https://www.kdhe.ks.gov/582/Home-Health-Agencies',
    questions: [
      {
        id: 'q1',
        question: 'Which of the following documents do you maintain in EVERY employee\'s personnel file? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'app', label: 'Employment application' },
          { value: 'id', label: 'Government-issued photo ID' },
          { value: 'license', label: 'Professional license verification (KSBN)' },
          { value: 'background', label: 'KBI background check clearance' },
          { value: 'abuse_registry', label: 'Kansas Nurse Aide Registry check' },
          { value: 'references', label: 'Reference verifications' },
          { value: 'health', label: 'Health screening/TB documentation' },
          { value: 'i9', label: 'I-9 Employment Eligibility Verification' },
          { value: 'orientation', label: 'Orientation documentation' },
          { value: 'training', label: 'Training and competency records' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 10, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 7, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 7, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Personnel files contain all Kansas-required documentation per K.A.R. 28-51-105.',
      atRisk: 'Some personnel files are missing required documents. Kansas requires: KBI background check, Nurse Aide Registry check, training records. Missing: {missing}.',
      nonCompliant: 'Personnel files are missing critical documentation required by K.A.R. 28-51-105. KDHE surveyors will cite incomplete files. Immediately obtain: {missing}.',
      unknown: 'Audit personnel files against K.A.R. 28-51-105 requirements.',
    },
    stateNotes: [
      'KBI handles criminal background checks in Kansas',
      'KSBN (Kansas State Board of Nursing) for license verification',
      'Kansas Nurse Aide Registry must be checked for abuse findings',
      'All documents must be in place BEFORE patient contact',
    ],
  },

  {
    id: 'KS-B2',
    itemId: 'B2',
    stateCode: 'KS',
    title: 'Home Health Aide Qualifications (K.A.R. 28-51-106)',
    description: 'Kansas requires specific qualifications for home health aides',
    severity: 'high',
    regulationCode: '28-51-106',
    regulationName: 'HHA Requirements',
    agencyName: 'Kansas Department of Health and Environment',
    agencyAbbr: 'KDHE',
    agencyPhone: '(785) 296-1086',
    agencyUrl: 'https://www.kdhe.ks.gov/582/Home-Health-Agencies',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL your home health aides meet Kansas qualification requirements? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'age', label: 'At least 18 years old' },
          { value: 'education', label: 'High school diploma or GED' },
          { value: 'training', label: 'Completed 75-hour training program (or CNA certification)' },
          { value: 'competency', label: 'Passed competency evaluation' },
          { value: 'cpr', label: 'Current CPR certification' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 5, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 3, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 3, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'All home health aides meet Kansas qualification requirements per K.A.R. 28-51-106.',
      atRisk: 'Some home health aides may not meet all qualification requirements. Review credentials immediately.',
      nonCompliant: 'Home health aides are working without proper qualifications. This violates K.A.R. 28-51-106. Remove unqualified aides from patient care.',
      unknown: 'Verify all home health aide qualifications against KDHE requirements.',
    },
    stateNotes: [
      'Kansas requires 75-hour training (same as federal Medicare requirement)',
      'CNA certification accepted as equivalent',
      'Competency evaluation must be documented',
      'CPR certification must be current',
    ],
  },

  {
    id: 'KS-B3',
    itemId: 'B3',
    stateCode: 'KS',
    title: 'Professional License Verification (KSBN)',
    description: 'Kansas requires verification of all professional licenses through KSBN',
    severity: 'high',
    regulationCode: 'K.A.R. 28-51-105',
    regulationName: 'License Verification',
    agencyName: 'Kansas State Board of Nursing',
    agencyAbbr: 'KSBN',
    agencyPhone: '(785) 296-4929',
    agencyUrl: 'https://ksbn.kansas.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL your nurses have current, active Kansas nursing licenses?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all nurses verified and current' },
          { value: 'most', label: 'Most are verified, some pending' },
          { value: 'no', label: 'No / not tracking this' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do you verify licenses through the KSBN online system BEFORE hire and track renewals?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes, verify before hire and track renewals' },
          { value: 'hire_only', label: 'Verify at hire only' },
          { value: 'no', label: 'No formal verification process' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'equals', questionId: 'q2', value: 'always' },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'most' },
        { condition: 'equals', questionId: 'q2', value: 'hire_only' },
      ], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'All professional licenses verified through KSBN with ongoing tracking.',
      atRisk: 'License verification or tracking is inconsistent. Verify all licenses and implement renewal tracking.',
      nonCompliant: 'Nurses working without verified Kansas licenses violates Kansas Nurse Practice Act. Verify all licenses immediately with KSBN.',
      unknown: 'Verify all professional licenses with KSBN.',
    },
    stateNotes: [
      'KSBN is Kansas\'s nursing license authority',
      'Free online license verification at ksbn.kansas.gov',
      'RN and LPN licenses renew every 2 years',
      'Kansas participates in Nurse Licensure Compact (NLC)',
    ],
  },

  {
    id: 'KS-B4',
    itemId: 'B4',
    stateCode: 'KS',
    title: 'HIPAA/Confidentiality Agreement Signed',
    description: 'Kansas requires signed HIPAA and confidentiality agreements',
    severity: 'high',
    regulationCode: '45 CFR Parts 160-164 / K.A.R. 28-51-105',
    regulationName: 'Personnel Records',
    agencyName: 'Kansas Department of Health and Environment',
    agencyAbbr: 'KDHE',
    agencyPhone: '(785) 296-1086',
    agencyUrl: 'https://www.kdhe.ks.gov/582/Home-Health-Agencies',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL your direct care workers have a signed HIPAA/confidentiality agreement on file?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all have signed agreements' },
          { value: 'most', label: 'Most have it, some are missing' },
          { value: 'no', label: 'No / not consistently maintained' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'equals', value: 'all', result: 'COMPLIANT' },
      { condition: 'equals', value: 'most', result: 'AT_RISK' },
      { condition: 'equals', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'All staff have signed HIPAA/confidentiality agreements on file.',
      atRisk: 'Some staff are missing signed HIPAA/confidentiality agreements. This creates significant privacy risk.',
      nonCompliant: 'Personnel files are missing required HIPAA/confidentiality agreements.',
      unknown: 'Verify all staff have signed HIPAA/confidentiality agreements on file.',
    },
    stateNotes: [
      'HIPAA compliance required under 45 CFR Parts 160-164',
      'K.A.R. 28-51-105 requires documentation in personnel files',
      'Agreement should be signed at orientation before patient contact',
      'Include consequences of confidentiality breaches in training',
    ],
  },

  // ============================================
  // SECTION C: BACKGROUND SCREENING
  // ============================================
  {
    id: 'KS-C1',
    itemId: 'C1',
    stateCode: 'KS',
    title: 'Criminal Background Check (K.A.R. 28-51-105)',
    description: 'Kansas requires KBI background checks including national fingerprint checks for all staff with patient contact',
    severity: 'high',
    regulationCode: 'K.A.R. 28-51-105',
    regulationName: 'Background Check Requirements',
    agencyName: 'Kansas Bureau of Investigation',
    agencyAbbr: 'KBI',
    agencyPhone: '(785) 296-8200',
    agencyUrl: 'https://www.kansas.gov/kbi/',
    questions: [
      {
        id: 'q1',
        question: 'For ALL currently employed staff with patient contact, which of the following do you have on file? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'kbi', label: 'Kansas Bureau of Investigation (KBI) state criminal check' },
          { value: 'fbi', label: 'FBI fingerprint-based national background check' },
          { value: 'sex_offender', label: 'Kansas Sex Offender Registry check' },
          { value: 'nurse_aide', label: 'Kansas Nurse Aide Registry check (abuse findings)' },
          { value: 'oig', label: 'OIG/SAM federal exclusion list verification' },
        ],
        required: true,
        helpText: 'Kansas requires background checks completed BEFORE patient contact',
      },
      {
        id: 'q2',
        question: 'Do any staff begin working with patients BEFORE their background check clearance is received?',
        inputType: 'binary',
        options: [
          { value: 'no', label: 'No - never before clearance' },
          { value: 'yes', label: 'Yes - sometimes they start early' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q2', value: 'yes', result: 'NON_COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'countAtLeast', questionId: 'q1', value: 5 },
        { condition: 'equals', questionId: 'q2', value: 'no' },
      ], result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q1', value: 3, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q1', value: 3, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Background check documentation is complete for all staff per K.A.R. 28-51-105.',
      atRisk: 'Background check documentation may be incomplete. Kansas requires clearance BEFORE any patient contact.',
      nonCompliant: 'Staff are working without proper background check clearance or are starting work before clearance is received. Employees working before background check clearance is a direct violation of K.A.R. 28-51-105. Immediately remove uncleared staff from patient assignments.',
      unknown: 'Audit all staff files for background check documentation per K.A.R. 28-51-105.',
    },
    stateNotes: [
      'KBI handles Kansas criminal background checks',
      'FBI fingerprint check required in addition to state check',
      'NO patient contact allowed until clearance received',
      'Kansas Sex Offender Registry: accesskansas.org/kbi/offenders',
      'Kansas Nurse Aide Registry must be checked for abuse findings',
    ],
  },

  {
    id: 'KS-C2',
    itemId: 'C2',
    stateCode: 'KS',
    title: 'Kansas Nurse Aide Registry Check',
    description: 'Kansas requires checking the Nurse Aide Registry for abuse/neglect findings',
    severity: 'high',
    regulationCode: 'K.A.R. 28-51-105',
    regulationName: 'Registry Verification',
    agencyName: 'Kansas Department of Health and Environment',
    agencyAbbr: 'KDHE',
    agencyPhone: '(785) 296-1086',
    agencyUrl: 'https://www.kdhe.ks.gov/582/Home-Health-Agencies',
    questions: [
      {
        id: 'q1',
        question: 'Do you verify all potential hires against the Kansas Nurse Aide Registry?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes, all hires verified before start date' },
          { value: 'sometimes', label: 'Sometimes' },
          { value: 'no', label: 'No / not familiar with this registry' },
        ],
        required: true,
        helpText: 'Registry contains substantiated abuse/neglect findings',
      },
    ],
    scoringRules: [
      { condition: 'equals', value: 'always', result: 'COMPLIANT' },
      { condition: 'equals', value: 'sometimes', result: 'AT_RISK' },
      { condition: 'equals', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Kansas Nurse Aide Registry checks are conducted for all hires.',
      atRisk: 'Registry verification is inconsistent. Verify all potential hires.',
      nonCompliant: 'Nurse Aide Registry checks are not being conducted. Persons with substantiated abuse findings cannot work in caregiving roles.',
      unknown: 'Contact KDADS for Nurse Aide Registry verification procedures.',
    },
    stateNotes: [
      'Kansas Nurse Aide Registry is maintained by KDADS (not KDHE)',
      'Registry contains substantiated abuse/neglect findings',
      'Persons on registry cannot work in caregiving roles',
      'Free online verification available through KDADS',
    ],
  },

  {
    id: 'KS-C3',
    itemId: 'C3',
    stateCode: 'KS',
    title: 'OIG/SAM Exclusion List Verification',
    description: 'Federal exclusion list checks required for all staff',
    severity: 'high',
    regulationCode: '42 CFR 1001',
    regulationName: 'Federal Exclusion Requirements',
    agencyName: 'Office of Inspector General',
    agencyAbbr: 'OIG',
    agencyPhone: '(800) 447-8477',
    agencyUrl: 'https://exclusions.oig.hhs.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do you verify ALL employees against the OIG and SAM exclusion lists?',
        inputType: 'select',
        options: [
          { value: 'monthly', label: 'Yes, at hire AND monthly thereafter' },
          { value: 'hire', label: 'Yes, at hire only' },
          { value: 'no', label: 'No / not consistently verified' },
        ],
        required: true,
        helpText: 'CMS requires monthly exclusion list checks for Medicare/Medicaid providers',
      },
    ],
    scoringRules: [
      { condition: 'equals', value: 'monthly', result: 'COMPLIANT' },
      { condition: 'equals', value: 'hire', result: 'AT_RISK' },
      { condition: 'equals', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'OIG/SAM exclusion list verification conducted at hire and monthly.',
      atRisk: 'Exclusion list verification at hire only is insufficient. Implement monthly checks.',
      nonCompliant: 'No OIG/SAM exclusion list verification conducted. This creates significant Medicare/Medicaid compliance risk.',
      unknown: 'Implement OIG/SAM exclusion list verification process.',
    },
    stateNotes: [
      'OIG LEIE: https://exclusions.oig.hhs.gov',
      'SAM.gov: https://sam.gov',
      'CMS requires monthly verification for Medicare/Medicaid providers',
      'Penalties can exceed $10,000 per service by excluded individual',
      'Document all exclusion checks with date and results',
    ],
  },

  // ============================================
  // SECTION D: HEALTH & SAFETY
  // ============================================
  {
    id: 'KS-D1',
    itemId: 'D1',
    stateCode: 'KS',
    title: 'TB Screening Requirements (K.A.R. 28-51-105)',
    description: 'Kansas requires TB screening for all staff with patient contact',
    severity: 'high',
    regulationCode: 'K.A.R. 28-51-105',
    regulationName: 'Health Requirements',
    agencyName: 'Kansas Department of Health and Environment',
    agencyAbbr: 'KDHE',
    agencyPhone: '(785) 296-1086',
    agencyUrl: 'https://www.kdhe.ks.gov/582/Home-Health-Agencies',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL staff with patient contact have TB screening documentation on file?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all staff have TB screening on file' },
          { value: 'most', label: 'Most do, some may be missing' },
          { value: 'no', label: 'No / not tracking this' },
        ],
        required: true,
        helpText: 'TB screening required at hire per K.A.R. 28-51-105',
      },
      {
        id: 'q2',
        question: 'How do you track TB screening dates?',
        inputType: 'select',
        options: [
          { value: 'system', label: 'Automated system with reminders' },
          { value: 'manual', label: 'Manual tracking (spreadsheet, calendar)' },
          { value: 'none', label: 'No active tracking system' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'includes', questionId: 'q2', value: ['system', 'manual'] },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'most' },
        { condition: 'equals', questionId: 'q2', value: 'none' },
      ], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'TB screening documentation is on file for all staff per K.A.R. 28-51-105.',
      atRisk: 'Some TB screening documentation may be missing. Review all staff files.',
      nonCompliant: 'Staff are working without TB screening documentation. This violates K.A.R. 28-51-105. Obtain screening immediately.',
      unknown: 'Verify TB screening status for all staff.',
    },
    stateNotes: [
      'Kansas requires TB screening at hire',
      'Follow CDC/KDHE guidelines for healthcare workers',
      'Positive TB tests require chest X-ray and medical clearance',
    ],
  },

  {
    id: 'KS-D2',
    itemId: 'D2',
    stateCode: 'KS',
    title: 'Infection Control Program (K.A.R. 28-51-102)',
    description: 'Kansas requires infection control procedures',
    severity: 'high',
    regulationCode: 'K.A.R. 28-51-102',
    regulationName: 'Infection Control Requirements',
    agencyName: 'Kansas Department of Health and Environment',
    agencyAbbr: 'KDHE',
    agencyPhone: '(785) 296-1086',
    agencyUrl: 'https://www.kdhe.ks.gov/582/Home-Health-Agencies',
    questions: [
      {
        id: 'q1',
        question: 'Which infection control components does your agency have in place? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'policy', label: 'Written infection control policies' },
          { value: 'training', label: 'Staff infection control training' },
          { value: 'ppe', label: 'PPE availability and training' },
          { value: 'hand_hygiene', label: 'Hand hygiene protocols' },
          { value: 'surveillance', label: 'Infection surveillance and reporting' },
          { value: 'outbreak', label: 'Outbreak management procedures' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 6, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 4, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 4, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Infection control program meets Kansas K.A.R. 28-51-102 requirements.',
      atRisk: 'Infection control program is missing some components. Review K.A.R. 28-51-102 requirements.',
      nonCompliant: 'Infection control program is inadequate per K.A.R. 28-51-102. Develop a comprehensive program immediately.',
      unknown: 'Review infection control program against K.A.R. 28-51-102.',
    },
    stateNotes: [
      'K.A.R. 28-51-102 requires written infection control procedures',
      'KDHE Bureau of Epidemiology provides guidance: (785) 296-2951',
      'Follow CDC guidelines for healthcare workers',
      'PPE training must be documented in personnel files',
    ],
  },

  {
    id: 'KS-D3',
    itemId: 'D3',
    stateCode: 'KS',
    title: 'Hepatitis B Vaccination (OSHA)',
    description: 'OSHA requires Hepatitis B vaccination offer for at-risk employees',
    severity: 'medium',
    regulationCode: '29 CFR 1910.1030',
    regulationName: 'Bloodborne Pathogens Standard',
    agencyName: 'Occupational Safety and Health Administration',
    agencyAbbr: 'OSHA',
    agencyPhone: '(800) 321-6742',
    agencyUrl: 'https://osha.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do you offer Hepatitis B vaccination to all staff with occupational exposure risk?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, offered to all at-risk staff' },
          { value: 'some', label: 'Offered to some staff' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do you document vaccination or signed declination for each employee?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'some', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Hepatitis B vaccination program meets OSHA requirements.',
      atRisk: 'Vaccination is not offered to all at-risk staff. Review OSHA compliance.',
      nonCompliant: 'Failure to offer Hepatitis B vaccination violates OSHA. Implement immediately.',
      unknown: 'Review Hepatitis B vaccination program.',
    },
    stateNotes: [
      'OSHA 29 CFR 1910.1030 requires employer to offer vaccination',
      'Vaccination must be offered within 10 days of assignment to at-risk work',
      'Declination form must be signed and kept in personnel file',
      'Employee may accept vaccination at any time at employer cost',
    ],
  },

  {
    id: 'KS-D4',
    itemId: 'D4',
    stateCode: 'KS',
    title: 'PPE Availability and Training',
    description: 'Kansas requires PPE availability and training for staff',
    severity: 'high',
    regulationCode: 'K.A.R. 28-51-102 / OSHA 29 CFR 1910.132',
    regulationName: 'Personal Protective Equipment Requirements',
    agencyName: 'Occupational Safety and Health Administration',
    agencyAbbr: 'OSHA',
    agencyPhone: '(800) 321-6742',
    agencyUrl: 'https://osha.gov',
    questions: [
      {
        id: 'q1',
        question: 'Is appropriate PPE available to ALL direct care staff?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, PPE is readily available to all staff' },
          { value: 'some', label: 'Available but not consistently' },
          { value: 'no', label: 'PPE is not provided' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do staff receive training on proper PPE use?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'some' },
        { condition: 'equals', questionId: 'q2', value: 'no' },
      ], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'PPE is available to all staff with proper training.',
      atRisk: 'PPE availability or training is inconsistent. Ensure all staff have access and training.',
      nonCompliant: 'PPE is not provided to staff. This violates OSHA requirements.',
      unknown: 'Verify PPE availability and training for all staff.',
    },
    stateNotes: [
      'OSHA 29 CFR 1910.132 requires PPE be provided at no cost',
      'K.A.R. 28-51-102 requires infection control procedures including PPE',
      'Document PPE training in personnel files',
      'Include gloves, gowns, masks, and eye protection as appropriate',
    ],
  },

  // ============================================
  // SECTION E: TRAINING & COMPETENCY
  // ============================================
  {
    id: 'KS-E1',
    itemId: 'E1',
    stateCode: 'KS',
    title: 'Home Health Aide Initial Training (K.A.R. 28-51-106)',
    description: 'Kansas requires 75 hours of training for home health aides',
    severity: 'high',
    regulationCode: 'K.A.R. 28-51-106',
    regulationName: 'Training Requirements',
    agencyName: 'Kansas Department of Health and Environment',
    agencyAbbr: 'KDHE',
    agencyPhone: '(785) 296-1086',
    agencyUrl: 'https://www.kdhe.ks.gov/582/Home-Health-Agencies',
    questions: [
      {
        id: 'q1',
        question: 'How many hours of initial training do ALL home health aides complete BEFORE patient contact?',
        inputType: 'number',
        required: true,
        helpText: 'Kansas requires 75 hours (or CNA certification equivalent)',
      },
      {
        id: 'q2',
        question: 'Does initial training include the following required components? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'classroom', label: 'Classroom instruction' },
          { value: 'clinical', label: 'Clinical/supervised practical training' },
          { value: 'competency', label: 'Competency evaluation passed' },
          { value: 'skills', label: 'Skills demonstration completed' },
          { value: 'documentation', label: 'Training documentation on file' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'greaterOrEqual', questionId: 'q1', value: 75 },
        { condition: 'countAtLeast', questionId: 'q2', value: 4 },
      ], result: 'COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'greaterOrEqual', questionId: 'q1', value: 40 },
        { condition: 'countAtLeast', questionId: 'q2', value: 3 },
      ], result: 'AT_RISK' },
      { condition: 'lessThan', questionId: 'q1', value: 40, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Home health aide training program meets Kansas 75-hour requirement per K.A.R. 28-51-106.',
      atRisk: 'Training program may be incomplete. Kansas requires 75 hours initial training.',
      nonCompliant: 'Home health aides are providing care without required 75-hour training. This violates K.A.R. 28-51-106.',
      unknown: 'Review training program against KDHE requirements.',
    },
    stateNotes: [
      'Kansas requires 75 hours initial training (same as federal Medicare)',
      'CNA certification accepted as equivalent',
      'Training must be completed BEFORE patient contact',
      'Must include both classroom and clinical components',
    ],
  },

  {
    id: 'KS-E2',
    itemId: 'E2',
    stateCode: 'KS',
    title: 'Annual In-Service Training (K.A.R. 28-51-106)',
    description: 'Kansas requires 12 hours of annual in-service training',
    severity: 'high',
    regulationCode: 'K.A.R. 28-51-106',
    regulationName: 'Continuing Education Requirements',
    agencyName: 'Kansas Department of Health and Environment',
    agencyAbbr: 'KDHE',
    agencyPhone: '(785) 296-1086',
    agencyUrl: 'https://www.kdhe.ks.gov/582/Home-Health-Agencies',
    questions: [
      {
        id: 'q1',
        question: 'How many hours of annual in-service training do ALL home health aides complete?',
        inputType: 'number',
        required: true,
        helpText: 'Kansas requires 12 hours annually',
      },
      {
        id: 'q2',
        question: 'Which required topics does your annual training cover? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'safety', label: 'Patient safety' },
          { value: 'infection', label: 'Infection control' },
          { value: 'emergency', label: 'Emergency procedures' },
          { value: 'abuse', label: 'Abuse recognition and reporting' },
          { value: 'rights', label: 'Patient rights' },
          { value: 'documentation', label: 'Documentation' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'greaterOrEqual', questionId: 'q1', value: 12 },
        { condition: 'countAtLeast', questionId: 'q2', value: 5 },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'greaterOrEqual', questionId: 'q1', value: 6 },
        { condition: 'countAtLeast', questionId: 'q2', value: 3 },
      ], result: 'AT_RISK' },
      { condition: 'lessThan', questionId: 'q1', value: 6, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Annual in-service training meets Kansas 12-hour requirement per K.A.R. 28-51-106.',
      atRisk: 'Some staff may be behind on annual training. Kansas requires 12 hours annually.',
      nonCompliant: 'Annual in-service training does not meet 12-hour requirement. Implement structured annual training immediately.',
      unknown: 'Verify annual in-service training compliance.',
    },
    stateNotes: [
      'Kansas requires 12 hours annual in-service (same as federal Medicare)',
      'Training records must be documented in personnel files',
      'Should include abuse reporting refresher annually',
    ],
  },

  {
    id: 'KS-E3',
    itemId: 'E3',
    stateCode: 'KS',
    title: 'Mandated Reporter Training (K.S.A. 39-1431)',
    description: 'All staff are mandated reporters of vulnerable adult abuse under Kansas law',
    severity: 'high',
    regulationCode: 'K.S.A. 39-1431',
    regulationName: 'Adult Abuse Reporting - Mandated Reporters',
    agencyName: 'Kansas Department for Children and Families',
    agencyAbbr: 'DCF',
    agencyPhone: '(800) 922-5330',
    agencyUrl: 'https://www.dcf.ks.gov/services/PPS/Pages/APS.aspx',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL your direct care workers receive training on mandated reporter responsibilities?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all staff are trained during orientation and annually' },
          { value: 'most', label: 'Most staff are trained' },
          { value: 'no', label: 'No / not sure' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do staff know how to report suspected abuse to the DCF/APS hotline?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes, APS number (800) 922-5330 is posted and trained' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'most' },
        { condition: 'equals', questionId: 'q2', value: 'no' },
      ], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'All staff are trained on mandated reporter duties per K.S.A. 39-1431.',
      atRisk: 'Some staff may lack mandated reporter training. All staff are mandated reporters under Kansas law.',
      nonCompliant: 'Failure to train staff on mandated reporter duties violates K.S.A. 39-1431. Staff who fail to report suspected abuse can face criminal penalties (Class B misdemeanor).',
      unknown: 'Verify all staff have received mandated reporter training.',
    },
    stateNotes: [
      'Kansas Adult Protective Services: (800) 922-5330',
      'Reports can be made 24/7',
      'All home health workers are mandated reporters',
      'Failure to report is a Class B misdemeanor under K.S.A. 39-1431',
    ],
  },

  {
    id: 'KS-E4',
    itemId: 'E4',
    stateCode: 'KS',
    title: 'Competency Evaluations (K.A.R. 28-51-106)',
    description: 'Kansas requires documented competency evaluations',
    severity: 'high',
    regulationCode: 'K.A.R. 28-51-106',
    regulationName: 'Competency Evaluation Requirements',
    agencyName: 'Kansas Department of Health and Environment',
    agencyAbbr: 'KDHE',
    agencyPhone: '(785) 296-1086',
    agencyUrl: 'https://www.kdhe.ks.gov/582/Home-Health-Agencies',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL your home health aides have documented competency evaluations on file?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all aides have initial and annual competency evaluations' },
          { value: 'most', label: 'Most aides have them' },
          { value: 'no', label: 'No / not tracking this' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Are competency evaluations performed by a licensed professional (RN)?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes, always by RN or appropriate professional' },
          { value: 'no', label: 'No / not always' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'most' },
        { condition: 'equals', questionId: 'q2', value: 'no' },
      ], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'All home health aides have documented competency evaluations per K.A.R. 28-51-106.',
      atRisk: 'Some competency evaluations may be missing or not performed by licensed professional.',
      nonCompliant: 'Home health aides are working without documented competency evaluations. This violates K.A.R. 28-51-106.',
      unknown: 'Verify all aides have competency evaluations.',
    },
    stateNotes: [
      'Initial competency must be verified before patient care assignment',
      'Annual competency evaluation required by K.A.R. 28-51-106',
      'RN must perform or delegate competency evaluation',
      'Skills checklist should document all required competencies',
    ],
  },

  // ============================================
  // SECTION F: CARE DELIVERY
  // ============================================
  {
    id: 'KS-F1',
    itemId: 'F1',
    stateCode: 'KS',
    title: 'Plan of Care (K.A.R. 28-51-107)',
    description: 'Kansas requires physician-ordered plans of care for all patients before services begin',
    severity: 'high',
    regulationCode: 'K.A.R. 28-51-107',
    regulationName: 'Plan of Care Requirements',
    agencyName: 'Kansas Department of Health and Environment',
    agencyAbbr: 'KDHE',
    agencyPhone: '(785) 296-1086',
    agencyUrl: 'https://www.kdhe.ks.gov/582/Home-Health-Agencies',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a physician-ordered plan of care for EVERY patient before services begin?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes, always before first visit' },
          { value: 'usually', label: 'Usually, but sometimes services start first' },
          { value: 'no', label: 'No formal care plan process' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do your plans of care include all Kansas-required elements? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'diagnosis', label: 'Patient diagnosis' },
          { value: 'services', label: 'Specific services to be provided' },
          { value: 'frequency', label: 'Frequency and duration of services' },
          { value: 'goals', label: 'Patient goals' },
          { value: 'physician', label: 'Physician signature' },
          { value: 'review', label: 'Review schedule (every 60 days)' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'always' },
        { condition: 'countAtLeast', questionId: 'q2', value: 5 },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'usually' },
        { condition: 'countAtLeast', questionId: 'q2', value: 3 },
      ], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Plans of care are completed before services begin and include all required elements per K.A.R. 28-51-107.',
      atRisk: 'Plans of care may be incomplete or created after services begin. Review K.A.R. 28-51-107 requirements.',
      nonCompliant: 'Services are being provided without physician-ordered plans of care. This violates K.A.R. 28-51-107. Immediately obtain care plans for all patients.',
      unknown: 'Review plan of care process against KDHE requirements.',
    },
    stateNotes: [
      'Plan of care must be in place BEFORE services begin',
      'Physician orders required for all services',
      'Must be reviewed at least every 60 days',
      'Patient/representative should acknowledge the plan',
    ],
  },

  {
    id: 'KS-F2',
    itemId: 'F2',
    stateCode: 'KS',
    title: 'RN Supervision of Home Health Aides (K.A.R. 28-51-106)',
    description: 'Kansas requires RN supervision of home health aides',
    severity: 'high',
    regulationCode: 'K.A.R. 28-51-106',
    regulationName: 'Supervision Requirements',
    agencyName: 'Kansas Department of Health and Environment',
    agencyAbbr: 'KDHE',
    agencyPhone: '(785) 296-1086',
    agencyUrl: 'https://www.kdhe.ks.gov/582/Home-Health-Agencies',
    questions: [
      {
        id: 'q1',
        question: 'Do you provide RN supervision of home health aides as required?',
        inputType: 'select',
        options: [
          { value: 'yes', label: 'Yes, per regulatory schedule' },
          { value: 'partial', label: 'Sometimes, not consistently' },
          { value: 'no', label: 'No supervision program' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'How often does RN supervision occur for home health aide services?',
        inputType: 'select',
        options: [
          { value: 'every_14', label: 'Every 14 days or more frequently' },
          { value: 'every_30', label: 'Every 30 days' },
          { value: 'every_60', label: 'Every 60 days' },
          { value: 'less', label: 'Less frequently' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'every_14', result: 'COMPLIANT' },
      { condition: 'includes', questionId: 'q2', value: ['every_30', 'every_60'], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q2', value: 'less', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'RN supervision meets Kansas K.A.R. 28-51-106 requirements (every 14 days).',
      atRisk: 'Supervision frequency may not meet requirements. Kansas requires RN supervisory visit at least every 14 days.',
      nonCompliant: 'Home health aides are not receiving required RN supervision. This violates K.A.R. 28-51-106. Implement supervision immediately.',
      unknown: 'Verify supervision practices against K.A.R. 28-51-106.',
    },
    stateNotes: [
      'Kansas requires RN supervision every 14 days for HHAs',
      'Supervision must be documented',
      'Consider rural service area logistics for supervision scheduling',
    ],
  },

  {
    id: 'KS-F3',
    itemId: 'F3',
    stateCode: 'KS',
    title: 'Patient Rights (K.A.R. 28-51-102)',
    description: 'Kansas requires patients be informed of their rights',
    severity: 'high',
    regulationCode: 'K.A.R. 28-51-102',
    regulationName: 'Patient Rights Requirements',
    agencyName: 'Kansas Department of Health and Environment',
    agencyAbbr: 'KDHE',
    agencyPhone: '(785) 296-1086',
    agencyUrl: 'https://www.kdhe.ks.gov/582/Home-Health-Agencies',
    questions: [
      {
        id: 'q1',
        question: 'Do you provide written patient rights to all patients at admission?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do patients sign acknowledgment of receiving patient rights?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Patient rights are provided and documented per K.A.R. 28-51-102.',
      atRisk: 'Patient rights acknowledgment is not documented. Implement signed acknowledgment.',
      nonCompliant: 'Patient rights are not being provided. This violates K.A.R. 28-51-102.',
      unknown: 'Verify patient rights process meets requirements.',
    },
    stateNotes: [
      'K.A.R. 28-51-102 requires written patient rights policy',
      'K.S.A. 65-5108 specifies patient rights for home health',
      'Rights must be provided before or at start of service',
      'Include KDHE contact information for filing complaints',
    ],
  },

  // ============================================
  // SECTION G: DOCUMENTATION
  // ============================================
  {
    id: 'KS-G1',
    itemId: 'G1',
    stateCode: 'KS',
    title: 'Document Expiration Tracking',
    description: 'System to track license, certification, and clearance expirations',
    severity: 'high',
    regulationCode: 'K.A.R. 28-51-108',
    regulationName: 'Record Management',
    agencyName: 'Kansas Department of Health and Environment',
    agencyAbbr: 'KDHE',
    agencyPhone: '(785) 296-1086',
    agencyUrl: 'https://www.kdhe.ks.gov/582/Home-Health-Agencies',
    questions: [
      {
        id: 'q1',
        question: 'Do you track expiration dates for all required documents? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'agency_license', label: 'Agency license (annual renewal)' },
          { value: 'insurance', label: 'Liability insurance' },
          { value: 'workers_comp', label: 'Workers\' compensation' },
          { value: 'nursing_licenses', label: 'Professional nursing licenses (KSBN)' },
          { value: 'cpr', label: 'CPR certifications' },
          { value: 'training', label: 'Annual training due dates' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do you receive advance reminders before documents expire?',
        inputType: 'select',
        options: [
          { value: 'auto', label: 'Yes, automated reminders 30+ days in advance' },
          { value: 'manual', label: 'Yes, manual review monthly' },
          { value: 'no', label: 'No proactive tracking' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'countAtLeast', questionId: 'q1', value: 5 },
        { condition: 'includes', questionId: 'q2', value: ['auto', 'manual'] },
      ], result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q1', value: 3, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q1', value: 3, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Document expiration tracking is comprehensive with proactive reminders.',
      atRisk: 'Expiration tracking is incomplete. Missing tracking for: {missing}. Implement tracking to prevent compliance lapses.',
      nonCompliant: 'No systematic document tracking in place. This creates significant compliance risk.',
      unknown: 'Implement document expiration tracking system.',
    },
    stateNotes: [
      'Agency license renews annually',
      'KSBN nursing licenses renew every 2 years',
      'Workers\' compensation required per K.S.A. 44-532',
    ],
  },

  {
    id: 'KS-G2',
    itemId: 'G2',
    stateCode: 'KS',
    title: 'Clinical Documentation (K.A.R. 28-51-108)',
    description: 'Kansas requires complete and timely clinical documentation',
    severity: 'high',
    regulationCode: 'K.A.R. 28-51-108',
    regulationName: 'Clinical Records Requirements',
    agencyName: 'Kansas Department of Health and Environment',
    agencyAbbr: 'KDHE',
    agencyPhone: '(785) 296-1086',
    agencyUrl: 'https://www.kdhe.ks.gov/582/Home-Health-Agencies',
    questions: [
      {
        id: 'q1',
        question: 'Which documentation practices does your agency follow? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'consent', label: 'Signed consent for treatment on file' },
          { value: 'assessment', label: 'Initial assessment documented' },
          { value: 'visit_notes', label: 'Visit notes for each service' },
          { value: 'signatures', label: 'All entries signed and dated' },
          { value: 'physician_orders', label: 'Physician orders documented' },
          { value: 'discharge', label: 'Discharge summary completed' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 6, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 4, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 4, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Clinical documentation practices meet Kansas K.A.R. 28-51-108 requirements.',
      atRisk: 'Documentation practices need improvement. Review K.A.R. 28-51-108 requirements.',
      nonCompliant: 'Documentation practices are inadequate per K.A.R. 28-51-108. Implement proper documentation procedures.',
      unknown: 'Review documentation against K.A.R. 28-51-108 requirements.',
    },
    stateNotes: [
      'K.A.R. 28-51-108 requires complete clinical documentation',
      'Visit notes should be completed within 24-48 hours of visit',
      'All entries must be signed and dated by the clinician',
      'Physician orders must be obtained and documented before services',
    ],
  },

  {
    id: 'KS-G3',
    itemId: 'G3',
    stateCode: 'KS',
    title: 'Audit-Ready Documentation',
    description: 'Kansas requires records be available for KDHE inspection',
    severity: 'medium',
    regulationCode: 'K.A.R. 28-51-108',
    regulationName: 'Records Availability',
    agencyName: 'Kansas Department of Health and Environment',
    agencyAbbr: 'KDHE',
    agencyPhone: '(785) 296-1086',
    agencyUrl: 'https://www.kdhe.ks.gov/582/Home-Health-Agencies',
    questions: [
      {
        id: 'q1',
        question: 'Are your patient and personnel files organized and accessible for KDHE inspection?',
        inputType: 'select',
        options: [
          { value: 'organized', label: 'Yes, all files are organized with consistent structure' },
          { value: 'mostly', label: 'Most files are organized, some need work' },
          { value: 'disorganized', label: 'Files are not well organized' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Could you produce any patient\'s or employee\'s complete file within 5 minutes if KDHE arrived for an unannounced inspection?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'organized' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'mostly' },
        { condition: 'equals', questionId: 'q2', value: 'no' },
      ], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'disorganized', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Files are well-organized and ready for KDHE inspection.',
      atRisk: 'File organization needs improvement. KDHE surveyors expect immediate access to complete files.',
      nonCompliant: 'Disorganized files will result in survey deficiencies. Implement a standardized file structure immediately.',
      unknown: 'Review file organization against KDHE survey requirements.',
    },
    stateNotes: [
      'KDHE may conduct unannounced inspections at any time',
      'K.A.R. 28-51-108 requires records be available for inspection',
      'Both patient and personnel files must be readily accessible',
      'Electronic records must be printable on demand',
    ],
  },
];

export default KANSAS_QUESTIONS;